import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    FaUser, 
    FaCalendarAlt, 
    FaClock, 
    FaCheck, 
    FaTimes, 
    FaBan, 
    FaSpinner,
    FaSearch,
    FaFilter,
    FaExclamationTriangle,
    FaChevronLeft,
    FaChevronRight,
    FaUserCircle,
    FaPlusCircle,
    FaEye,
    FaFileAlt,
    FaDownload,
    FaUpload
} from 'react-icons/fa';
import Topbar from '../layouts/Topbar';
import Sidebar from '../layouts/Sidebar';
import './DisciplinaryRecords.css';

const DisciplinaryRecords = () => {
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [departmentFilter, setDepartmentFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [departments, setDepartments] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedEmployeeForAdd, setSelectedEmployeeForAdd] = useState(null);
    const [formData, setFormData] = useState({
        record_type: '',
        record_file: null,
        record_description: '',
        record_date: '',
        employee: ''
    });
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState(null);
    const baseUrl = 'https://api.burrayhr.com';
    const employeesPerPage = 100;

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const token = localStorage.getItem('token');
            
            // Fetch employees
            const employeesResponse = await axios.get(`${baseUrl}/api/employees/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setEmployees(employeesResponse.data);
            
            // Fetch departments
            const departmentsResponse = await axios.get(`${baseUrl}/api/departments/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            const departmentOptions = departmentsResponse.data.map(dept => ({
                value: dept.id,
                label: dept.name
            }));
            
            setDepartments(departmentOptions);
            setError(null);
        } catch (error) {
            console.error('Error fetching data:', error);
            setError('Failed to fetch data. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1); // Reset to first page on search
    };

    const handleDepartmentFilterChange = (e) => {
        setDepartmentFilter(e.target.value);
        setCurrentPage(1);
    };

    const handleCategoryFilterChange = (e) => {
        setCategoryFilter(e.target.value);
        setCurrentPage(1);
    };

    const filteredEmployees = employees.filter(employee => {
        const matchesSearch = 
            employee.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            employee.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (employee.department_name || '').toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesCategory = 
            categoryFilter === 'all' || 
            (employee.category && employee.category.toLowerCase() === categoryFilter.toLowerCase());
        
        const matchesDepartment = 
            departmentFilter === 'all' || 
            (employee.department_name && employee.department_name.toString() === departmentFilter);
        
        return matchesSearch && matchesCategory && matchesDepartment;
    });

    // Calculate pagination
    const indexOfLastEmployee = currentPage * employeesPerPage;
    const indexOfFirstEmployee = indexOfLastEmployee - employeesPerPage;
    const currentEmployees = filteredEmployees.slice(indexOfFirstEmployee, indexOfLastEmployee);
    const totalPages = Math.ceil(filteredEmployees.length / employeesPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleViewDocuments = (employee) => {
        setSelectedEmployee(employee);
    };

    const handleCloseModal = () => {
        setSelectedEmployee(null);
    };

    const handleViewRecordDetails = (record) => {
        setSelectedRecord(record);
    };

    const handleCloseRecordModal = () => {
        setSelectedRecord(null);
    };

    const handleAddClick = (employee) => {
        setSelectedEmployeeForAdd(employee);
        setFormData({
            record_type: '',
            record_file: null,
            record_description: '',
            record_date: '',
            employee: employee.id
        });
        setShowAddModal(true);
    };

    const handleCloseAddModal = () => {
        setShowAddModal(false);
        setSelectedEmployeeForAdd(null);
        setFormData({
            record_type: '',
            record_file: null,
            record_description: '',
            record_date: '',
            employee: ''
        });
        setFormError(null);
    };

    const handleInputChange = (e) => {
        const { name, value, files } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: files ? files[0] : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormLoading(true);
        setFormError(null);

        try {
            const token = localStorage.getItem('token');
            const formDataToSend = new FormData();
            
            // Append all form fields to FormData
            Object.keys(formData).forEach(key => {
                if (formData[key] !== null) {
                    formDataToSend.append(key, formData[key]);
                }
            });

            const response = await axios.post(
                `${baseUrl}/api/disciplinary-records/`,
                formDataToSend,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            // Update the employee's records in the state
            setEmployees(prevEmployees => 
                prevEmployees.map(emp => 
                    emp.id === selectedEmployeeForAdd.id 
                        ? {
                            ...emp,
                            disciplinary_records: [
                                ...(emp.disciplinary_records || []),
                                response.data
                            ]
                        }
                        : emp
                )
            );

            handleCloseAddModal();
        } catch (error) {
            console.error('Error adding disciplinary record:', error);
            setFormError(error.response?.data?.message || 'Failed to add disciplinary record. Please try again.');
        } finally {
            setFormLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
        const month = monthNames[date.getMonth()];
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    const getDocumentType = (documentType) => {
        switch(documentType?.toLowerCase()) {
            case 'warning':
                return { label: 'Warning', color: 'warning' };
            case 'suspension':
                return { label: 'Suspension', color: 'suspension' };
            case 'termination':
                return { label: 'Termination', color: 'termination' };
            case 'notice':
                return { label: 'Notice', color: 'notice' };
            default:
                return { label: documentType || 'Document', color: 'default' };
        }
    };

    const renderRecordModal = () => {
        if (!selectedRecord) return null;

        const docType = getDocumentType(selectedRecord.record_type);

        return (
            <div className="disciplinary-record-modal-overlay" onClick={handleCloseRecordModal}>
                <div className="disciplinary-record-modal-content" onClick={e => e.stopPropagation()}>
                    <div className="disciplinary-record-modal-header">
                        <div className="disciplinary-record-modal-title">
                            <h3>Document Details</h3>
                            <span className={`disciplinary-record-modal-type ${docType.color}`}>
                                {docType.label}
                            </span>
                        </div>
                        <button className="disciplinary-record-modal-close" onClick={handleCloseRecordModal}>
                            <FaTimes />
                        </button>
                    </div>
                    <div className="disciplinary-record-modal-body">
                        <div className="disciplinary-record-info">
                            <div className="disciplinary-record-date">
                                <FaCalendarAlt />
                                <span>{formatDate(selectedRecord.record_date)}</span>
                            </div>
                            {selectedRecord.record_description && (
                                <div className="disciplinary-record-description">
                                    <h4>Description</h4>
                                    <p>{selectedRecord.record_description}</p>
                                </div>
                            )}
                        </div>
                        {selectedRecord.record_file && (
                            <div className="disciplinary-record-actions">
                                <a 
                                    href={`${selectedRecord.record_file}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="disciplinary-record-open-button"
                                >
                                    <FaFileAlt />
                                    Open Document
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderAddModal = () => {
        if (!showAddModal) return null;

        return (
            <div className="disciplinary-modal-overlay" onClick={handleCloseAddModal}>
                <div className="disciplinary-modal-content" onClick={e => e.stopPropagation()}>
                    <div className="disciplinary-modal-header">
                        <div className="disciplinary-modal-title">
                            <h2>Add Disciplinary Record</h2>
                            <p className="disciplinary-modal-subtitle">
                                {selectedEmployeeForAdd?.first_name} {selectedEmployeeForAdd?.last_name}
                            </p>
                        </div>
                        <button className="disciplinary-modal-close" onClick={handleCloseAddModal}>
                            <FaTimes />
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="disciplinary-add-form">
                        <div className="disciplinary-form-body">
                            {formError && (
                                <div className="disciplinary-form-error">
                                    <FaExclamationTriangle />
                                    <p>{formError}</p>
                                </div>
                            )}

                            <div className="disciplinary-form-group">
                                <label htmlFor="record_type">Record Type</label>
                                <select
                                    id="record_type"
                                    name="record_type"
                                    value={formData.record_type}
                                    onChange={handleInputChange}
                                    required
                                    className="disciplinary-form-select"
                                >
                                    <option value="">Select Record Type</option>
                                    <option value="warning">Warning</option>
                                    <option value="suspension">Suspension</option>
                                    <option value="termination">Termination</option>
                                    <option value="notice">Notice</option>
                                </select>
                            </div>

                            <div className="disciplinary-form-group">
                                <label htmlFor="record_date">Record Date</label>
                                <input
                                    type="date"
                                    id="record_date"
                                    name="record_date"
                                    value={formData.record_date}
                                    onChange={handleInputChange}
                                    required
                                    className="disciplinary-form-input"
                                />
                            </div>

                            <div className="disciplinary-form-group">
                                <label htmlFor="record_description">Description</label>
                                <textarea
                                    id="record_description"
                                    name="record_description"
                                    value={formData.record_description}
                                    onChange={handleInputChange}
                                    required
                                    className="disciplinary-form-textarea"
                                    rows="4"
                                    placeholder="Enter record description..."
                                />
                            </div>

                            <div className="disciplinary-form-group">
                                <label htmlFor="record_file" className="disciplinary-file-label">
                                    <FaUpload />
                                    <span>Upload Document</span>
                                </label>
                                <input
                                    type="file"
                                    id="record_file"
                                    name="record_file"
                                    onChange={handleInputChange}
                                    className="disciplinary-form-file"
                                    accept=".pdf,.doc,.docx,.txt"
                                />
                                {formData.record_file && (
                                    <p className="disciplinary-file-name">
                                        Selected: {formData.record_file.name}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="disciplinary-form-actions">
                            <button
                                type="button"
                                className="disciplinary-form-cancel"
                                onClick={handleCloseAddModal}
                                disabled={formLoading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="disciplinary-form-submit"
                                disabled={formLoading}
                            >
                                {formLoading ? (
                                    <>
                                        <FaSpinner className="disciplinary-form-spinner" />
                                        Adding Record...
                                    </>
                                ) : (
                                    'Add Record'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    };

    const renderModal = () => {
        if (!selectedEmployee) return null;

        return (
            <div className="disciplinary-modal-overlay" onClick={handleCloseModal}>
                <div className="disciplinary-modal-content" onClick={e => e.stopPropagation()}>
                    <div className="disciplinary-modal-header">
                        <div className="disciplinary-modal-title">
                            <h2>Document Records</h2>
                            <p className="disciplinary-modal-subtitle">
                                {selectedEmployee.first_name} {selectedEmployee.last_name} - {selectedEmployee.department_name || 'No Department'}
                            </p>
                        </div>
                        <button className="disciplinary-modal-close" onClick={handleCloseModal}>
                            <FaTimes />
                        </button>
                    </div>

                    <div className="disciplinary-modal-body">
                        {selectedEmployee.disciplinary_records?.length > 0 ? (
                            <div className="disciplinary-documents-list">
                                {selectedEmployee.disciplinary_records.map((record, index) => {
                                    const docType = getDocumentType(record.record_type);
                                    return (
                                        <div key={index} className="disciplinary-document-item">
                                            <div className={`disciplinary-document-icon ${docType.color}`}>
                                                <FaFileAlt />
                                            </div>
                                            <div className="disciplinary-document-info">
                                                <div className="disciplinary-document-header">
                                                    
                                                    <span className={`disciplinary-document-type ${docType.color}`}>
                                                        {docType.label}
                                                    </span>
                                                </div>
                                                <div className="disciplinary-document-meta">
                                                    <span className="disciplinary-document-date">
                                                        <FaCalendarAlt />
                                                        {formatDate(record.record_date)}
                                                    </span>
                                                </div>
                                            </div>
                                            <button 
                                                className="disciplinary-document-view"
                                                onClick={() => handleViewRecordDetails(record)}
                                                title="View Document Details"
                                            >
                                                <FaEye />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="disciplinary-modal-empty">
                                <FaFileAlt className="disciplinary-modal-empty-icon" />
                                <p>No document records found for this employee.</p>
                            </div>
                        )}
                    </div>
                </div>
                {renderRecordModal()}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="disciplinary-loading-container">
                <Topbar/>
                <Sidebar/>
                <div className="disciplinary-loading-content">
                    <FaSpinner className="disciplinary-spinner" />
                    <p>Loading disciplinary records...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="disciplinary-error-container">
                <Topbar/>
                <Sidebar/>
                <div className="disciplinary-error-content">
                    <FaExclamationTriangle className="disciplinary-error-icon" />
                    <p>{error}</p>
                    <button onClick={fetchData} className="disciplinary-retry-button">
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="disciplinary-records-container">
            <Topbar />
            <Sidebar />
            <div className="disciplinary-main-content">
                <div className="disciplinary-header">
                    <div className="disciplinary-title-section">
                        <h1>
                            <FaUser className="disciplinary-title-icon" />
                            Disciplinary Records
                        </h1>
                        <p className="disciplinary-subtitle">View and manage employee disciplinary records</p>
                    </div>
                </div>

                <div className="disciplinary-search-section">
                    <div className="disciplinary-search-box">
                        <FaSearch className="disciplinary-search-icon" />
                        <input
                            type="text"
                            placeholder="Search employees..."
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                    </div>
                    <div className="disciplinary-filter-box">
                        <FaFilter className="disciplinary-filter-icon" />
                        <select 
                            value={categoryFilter} 
                            onChange={handleCategoryFilterChange}
                            className="disciplinary-category-filter"
                        >
                            <option value="all">All Categories</option>
                            <option value="worker">Workers</option>
                            <option value="managerial">Managerial</option>
                        </select>
                    </div>
                    <div className="disciplinary-filter-box">
                        <FaFilter className="disciplinary-filter-icon" />
                        <select 
                            value={departmentFilter} 
                            onChange={handleDepartmentFilterChange}
                            className="disciplinary-department-filter"
                        >
                            <option value="all">All Departments</option>
                            {departments.map(dept => (
                                <option key={dept.value} value={dept.label}>
                                    {dept.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="disciplinary-table-container">
                    <table className="disciplinary-table">
                        <thead>
                            <tr>
                                <th className="disciplinary-table-header">Employee</th>
                                <th className="disciplinary-table-header">Department</th>
                                <th className="disciplinary-table-header">Position</th>
                                <th className="disciplinary-table-header">Category</th>
                                <th className="disciplinary-table-header">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentEmployees.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="disciplinary-no-results">
                                        No employees found
                                    </td>
                                </tr>
                            ) : (
                                currentEmployees.map(employee => (
                                    <tr key={employee.id} className="disciplinary-table-row">
                                        <td className="disciplinary-table-cell">
                                            <div className="disciplinary-employee-info">
                                                {employee.photo_url ? (
                                                    <img 
                                                        src={employee.photo_url} 
                                                        alt={`${employee.first_name} ${employee.last_name}`}
                                                        className="disciplinary-employee-photo"
                                                    />
                                                ) : (
                                                    <FaUserCircle className="disciplinary-default-photo" />
                                                )}
                                                <div className="disciplinary-employee-details">
                                                    <span className="disciplinary-employee-name">
                                                        {employee.first_name} {employee.last_name}
                                                    </span>
                                                    <span className="disciplinary-employee-email">
                                                        {employee.email}
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="disciplinary-table-cell">
                                            {employee.department_name || 'No Department'}
                                        </td>
                                        <td className="disciplinary-table-cell">
                                            {employee.position || 'Not specified'}
                                        </td>
                                        <td className="disciplinary-table-cell">
                                            <span className={`disciplinary-category-badge ${employee.category?.toLowerCase()}`}>
                                                {employee.category || 'Not specified'}
                                            </span>
                                        </td>
                                        <td className="disciplinary-table-cell">
                                            <div className="disciplinary-action-buttons">
                                                <button 
                                                    className="disciplinary-action-button view"
                                                    title="View Document Records"
                                                    onClick={() => handleViewDocuments(employee)}
                                                >
                                                    <FaEye />
                                                </button>
                                                <button 
                                                    className="disciplinary-action-button add"
                                                    title="Add Document Record"
                                                    onClick={() => handleAddClick(employee)}
                                                >
                                                    <FaPlusCircle />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="disciplinary-pagination">
                        <button
                            className="disciplinary-pagination-button"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            <FaChevronLeft />
                        </button>
                        
                        {[...Array(totalPages)].map((_, index) => (
                            <button
                                key={index + 1}
                                className={`disciplinary-pagination-button ${currentPage === index + 1 ? 'active' : ''}`}
                                onClick={() => handlePageChange(index + 1)}
                            >
                                {index + 1}
                            </button>
                        ))}
                        
                        <button
                            className="disciplinary-pagination-button"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        >
                            <FaChevronRight />
                        </button>
                    </div>
                )}

                <div className="disciplinary-pagination-info">
                    Showing {indexOfFirstEmployee + 1} to {Math.min(indexOfLastEmployee, filteredEmployees.length)} of {filteredEmployees.length} employees
                </div>
            </div>
            {renderModal()}
            {renderAddModal()}
        </div>
    );
};

export default DisciplinaryRecords;
